import { Heading, RadioInput } from '@mm/components';
import { useInstagramCRUDActions } from '@mm/state/actions/InstagramCRUDActions';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import ConnectedPageModel from '@mm/state/model/ConnectedPageModel';
import { PromoterModel } from '@mm/state/model/PromoterModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { selectInstagramMedia } from '@mm/state/selectors/instagramSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import classnames from 'classnames';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import KeywordsForm from '../../commentGuards/components/EditFBPost/KeywordsForm/KeywordsForm';
import './InstagramPostSelectionForm.pcss';
import closeIcon from 'assets/icons/close.svg';

type Props = {
  guard: PromoterModel;
  facebookPage?: ConnectedPageModel;
  setToggler?: Dispatch<SetStateAction<'all' | 'keywords'>>;
  keywordIputPlaceholder?: string;
  customAddKeywordBtnName?: string;
  showKeywordsBroadMatch?: boolean;
  keywordInfoIconColor?: string;
  customRespondAllName?: string;
  customRespondToCommentsName?: string;
  customKeywordBroadPosition?: 'top' | 'bottom' | 'right' | 'left';
  customKeywordBroadAlign?: 'start' | 'center' | 'end';
};

const InstagramPostSelectionForm = (props: Props) => {
  const {
    guard,
    facebookPage,
    setToggler,
    keywordIputPlaceholder,
    showKeywordsBroadMatch,
    customAddKeywordBtnName,
    keywordInfoIconColor,
    customRespondAllName,
    customRespondToCommentsName,
    customKeywordBroadPosition,
    customKeywordBroadAlign,
  } = props;
  const chatbotId = useSelector(activeChatbotIdSelector);
  const { update } = usePromoterCRUDActions();
  const { openInstagramMediaModal } = useModalActions();
  const availableInstagramMedia = useSelector(selectInstagramMedia);
  const { getIGListMedia } = useInstagramCRUDActions();
  const selectedMedia = availableInstagramMedia.find(
    media => guard.postId === media.id,
  );

  const className = classnames('comment-guard-keywords', {
    'comment-guard-keywords--updated': guard,
  });

  const [showPostContainer, setShowPostContainer] = useState(
    guard.postId !== 'ALL',
  );

  const isImagePreview =
    selectedMedia?.mediaType === 'IMAGE' ||
    selectedMedia?.mediaType === 'CAROUSEL_ALBUM' ||
    (selectedMedia?.mediaProductType === 'REELS' && !selectedMedia.mediaUrl) ||
    (selectedMedia?.mediaType === 'VIDEO' && !selectedMedia.mediaUrl);

  useEffect(() => {
    if (!guard.postId || guard.postId === 'ALL') {
      setShowPostContainer(false);
    }
  }, []);

  const updatePromoter = (postId: string) => {
    if (guard) {
      update(chatbotId, {
        ...guard,
        postId,
      });
    }
  };

  return (
    <div className={className}>
      <div className="comment-guard-keywords__settings-container">
        <RadioInput
          className="comment-guard-post-form__radio-input"
          name="postId"
          checked={!showPostContainer}
          value="ALL"
          label="All Posts"
          css={{ input: 'comment-guard-post-form__radio-input-label' }}
          onChange={() => {
            setShowPostContainer(false);
            updatePromoter('ALL');
          }}
        />
        <RadioInput
          className="comment-guard-post-form__radio-input"
          name="postId"
          checked={showPostContainer}
          value="id"
          label="Specific Instagram Post"
          css={{ input: 'comment-guard-post-form__radio-input-label' }}
          onChange={() => {
            setShowPostContainer(true);
            updatePromoter('id');
          }}
        />
      </div>

      <div className="comment-guard-post-form__post-block-wrapper">
        {showPostContainer && (
          <div className="comment-guard-post-form__post-block">
            <Heading
              className="comment-guard-post-form__post-block-header"
              size={4}
              isBold={false}
            >
              Instagram Post for tracking
            </Heading>
            <div className="comment-guard-post-form__post-container">
              {isImagePreview ? (
                <img
                  className="comment-guard-post-form__post-empty-state-container"
                  src={selectedMedia?.mediaUrl || selectedMedia?.thumbnailUrl}
                />
              ) : selectedMedia?.mediaType === 'VIDEO' ? (
                <video
                  className="comment-guard-post-form__post-empty-state-container"
                  src={selectedMedia?.mediaUrl}
                  muted
                />
              ) : (
                <div className="comment-guard-post-form__post-empty-state-container">
                  <span className="comment-guard-post-form__post-empty-state">
                    Post not selected
                  </span>
                </div>
              )}
              <div className="comment-guard-post-form__selected-post-details">
                <PrimaryButton
                  color="secondary-blue"
                  onClick={() => {
                    openInstagramMediaModal(guard);
                    if (facebookPage && !selectedMedia) {
                      getIGListMedia?.(facebookPage?.id);
                    }
                  }}
                >
                  {guard.postId === 'id' || guard.postId === 'ALL'
                    ? 'Choose post'
                    : 'Change Post'}
                </PrimaryButton>
                {selectedMedia?.permalink && (
                  <a
                    className="comment-guard-post-form__selected-post-link"
                    href={selectedMedia.permalink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View on Instagram
                  </a>
                )}
              </div>
            </div>
          </div>
        )}
        <KeywordsForm
          guard={guard}
          useUpdatedDesign
          allowPhraseKeywords
          setToggler={setToggler}
          keywordsWrapperClassName="keywords_form"
          inputPlaceholder={keywordIputPlaceholder}
          showKeywordsBroadMatch={showKeywordsBroadMatch}
          customAddBtnName={customAddKeywordBtnName}
          infoIconColor={keywordInfoIconColor}
          customRespondAllName={customRespondAllName}
          customRespondToCommentsName={customRespondToCommentsName}
          customKeywordBroadPosition={customKeywordBroadPosition}
          customKeywordBroadAlign={customKeywordBroadAlign}
          customDeleteKeywordIcon={
            <img
              src={closeIcon}
              alt=""
              className="comment-guard-post-form__delete-keyword-icon"
            />
          }
        />
      </div>
    </div>
  );
};

export default InstagramPostSelectionForm;
